import React, { useContext } from 'react';
import './About.css';
import { useTranslation } from 'react-i18next';
import { FaCheckCircle, FaLeaf, FaCoffee, FaUsers } from 'react-icons/fa';
import { AiOutlineArrowDown } from 'react-icons/ai';
import aboutImg from '../../assets/img/about.png';
import Breadcrumb from '../../components/Breadcrumb/Breadcrumb';
import Button from '../../components/UI/Button/Button';
import LanguageContext from '../../context/LanguageContext.js';
import { usePublicSettings } from '../../context/PublicSettingsContext.js';

const About = () => {
    const { dir } = useContext(LanguageContext);
    const { publicSettings } = usePublicSettings();
    const { t, i18n } = useTranslation();
    const currentLanguage = i18n.language;

    const businessName = publicSettings?.businessName || 'Quazbit';

    const stats = [
        { valueKey: 'about.statYearsValue',  labelKey: 'about.statYearsLabel' },
        { valueKey: 'about.statMenuValue',   labelKey: 'about.statMenuLabel' },
        { valueKey: 'about.statGuestsValue', labelKey: 'about.statGuestsLabel' },
        { valueKey: 'about.statTeamValue',   labelKey: 'about.statTeamLabel' },
    ];

    const pillars = [
        { Icon: FaLeaf,      titleKey: 'about.farmFirstTitle',  bodyKey: 'about.farmFirstBody' },
        { Icon: FaCoffee, titleKey: 'about.drinksTitle',     bodyKey: 'about.drinksBody' },
        { Icon: FaUsers,     titleKey: 'about.gatheringsTitle', bodyKey: 'about.gatheringsBody' },
    ];

    const checklist = [
        'about.detail1',
        'about.detail2',
        'about.detail3',
        'about.detail4',
    ];

    return (
        <main className="main about-page">
            <Breadcrumb title={t('about.title', { businessName })} />

            {/* ══ HERO ══════════════════════════════════════ */}
            <section className="ab-hero" aria-labelledby="ab-hero-h">
                <div className="ab-noise" aria-hidden="true" />
                <span className="ab-watermark" aria-hidden="true">{t('about.watermark')}</span>

                <div className="container ab-hero-grid" dir={dir()}>

                    {/* Image column */}
                    <div className="ab-img-col">
                        <div className="ab-img-accent" aria-hidden="true" />
                        <div className="ab-img-frame">
                            <img
                                src={aboutImg}
                                alt={t('about.heroImageAlt', { businessName })}
                                className="ab-img"
                                decoding="async"
                            />
                            <div className="ab-img-overlay" aria-hidden="true" />
                        </div>
                        <div className="ab-since-badge" aria-hidden="true">
                            <span className="ab-since-label">{t('about.established')}</span>
                            <span className="ab-since-year">2012</span>
                        </div>
                        <div className="ab-vert-rule" aria-hidden="true">
                            <span /><em>{t('about.storyRail')}</em><span />
                        </div>
                    </div>

                    {/* Text column */}
                    <div className="ab-text-col">
                        <p className="ab-kicker">{t('about.heroSubtitle')}</p>
                        <h2 id="ab-hero-h" className="ab-h2">{t('about.title', { businessName })}</h2>
                        <div className="ab-squiggle" aria-hidden="true">
                            <svg viewBox="0 0 120 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M0 6 Q15 0 30 6 Q45 12 60 6 Q75 0 90 6 Q105 12 120 6"
                                      stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round"/>
                            </svg>
                        </div>
                        <p className="ab-lead">{t('about.description', { businessName })}</p>
                        <ul className="ab-checklist">
                            {checklist.map((key, i) => (
                                <li key={key} className="ab-check-item" style={{ '--d': `${i * 80}ms` }}>
                                    <FaCheckCircle className="ab-check-ico" aria-hidden="true" />
                                    <span>{t(key)}</span>
                                </li>
                            ))}
                        </ul>
                        <div className="ab-actions">
                            <Button to={`/${currentLanguage}/menu`} variant="outline" flex className="ab-btn ab-btn--ghost">
                                {t('about.learnMore')}
                                <AiOutlineArrowDown className="ab-btn-ico" aria-hidden="true" />
                            </Button>
                            <Button to={`/${currentLanguage}/reservation`} flex className="ab-btn ab-btn--solid">
                                {t('about.bookTable')}
                            </Button>
                        </div>
                    </div>
                </div>
            </section>

            {/* ══ PILLARS ═══════════════════════════════════ */}
            <section className="ab-pillars" aria-labelledby="ab-pillars-h">
                <div className="ab-noise" aria-hidden="true" />
                <div className="container ab-pillars-inner" dir={dir()}>
                    <header className="ab-section-head">
                        <div className="ab-head-rule" aria-hidden="true" />
                        <div className="ab-head-text">
                            <p className="ab-kicker ab-kicker--warm">{t('about.howWeWorkSubtitle')}</p>
                            <h2 id="ab-pillars-h" className="ab-h2 ab-h2--light">{t('about.howWeWorkTitle')}</h2>
                        </div>
                        <p className="ab-section-lead">{t('about.howWeWorkIntro')}</p>
                    </header>
                    <ul className="ab-pillars-grid">
                        {pillars.map(({ Icon, titleKey, bodyKey }, i) => (
                            <li key={titleKey} className="ab-pillar" style={{ '--i': i }}>
                                <div className="ab-pillar-num" aria-hidden="true">0{i + 1}</div>
                                <div className="ab-pillar-ico-wrap" aria-hidden="true"><Icon /></div>
                                <h3 className="ab-pillar-title">{t(titleKey)}</h3>
                                <p className="ab-pillar-body">{t(bodyKey)}</p>
                                <div className="ab-pillar-line" aria-hidden="true" />
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            {/* ══ STATS ══════════════════════════════════════ */}
            <section className="ab-stats" aria-labelledby="ab-stats-h">
                <div className="container ab-stats-inner" dir={dir()}>
                    <header className="ab-stats-head">
                        <h2 id="ab-stats-h" className="ab-h2 ab-h2--center">{t('about.byTheNumbersTitle')}</h2>
                        <p className="ab-stats-sub">{t('about.byTheNumbersSubtitle')}</p>
                    </header>
                    <ul className="ab-stats-grid">
                        {stats.map(({ valueKey, labelKey }, i) => (
                            <li key={labelKey} className="ab-stat" style={{ '--i': i }}>
                                <span className="ab-stat-val">{t(valueKey)}</span>
                                <span className="ab-stat-lbl">{t(labelKey)}</span>
                            </li>
                        ))}
                    </ul>
                </div>
                <p className="ab-stats-quote" aria-hidden="true">{t('about.craftedWithLove')}</p>
            </section>
        </main>
    );
};

export default About;